import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../exercise-types/index.ts';
import { api } from '../../lib/api/client.js';
import type { LessonReviewResponse, ReviewedExercise } from '../../lib/api/types.js';
import { useCurrentUser } from '../../app/AuthProvider.tsx';
import { describeAnswer } from '../exercise-types/registry.ts';
import { Dialog } from '../../ui/components/Dialog/Dialog.tsx';
import { LoadingState } from '../../ui/components/LoadingState/LoadingState.tsx';
import { useAsyncData } from '../../ui/components/LoadingState/useAsyncData.ts';
import { OfflineState } from '../../ui/components/OfflineState/OfflineState.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import { DEFAULT_LOCALE, type SupportedLocale } from '@cavaquinho/shared';
import styles from './content.module.css';

/**
 * `/revisao` — how the Lesson went, and the way back in.
 *
 * FINISHING A LESSON USED TO CLOSE IT. The cursor moved on, the spine drew a
 * green tick, and that tick was the entire record. This screen is the record,
 * and it is the one screen in the product that looks BACKWARDS — which is why
 * it gets an address of its own rather than a panel on the spine. The spine
 * answers "where am I", in one continuous column, and what happened inside a
 * finished Lesson pushed into one of its nodes would bury that question.
 *
 * WHICH LESSON, THOUGH, IS NOT IN THE ADDRESS. `/revisao` names no more than
 * `/praticar` does, because the learner has one position and the platform is
 * what holds it. The spine hands this screen a Lesson in navigation state when
 * the learner opens a finished node; arriving with nothing — a refresh, a
 * typed address, the "rever a última lição" at the foot of a finished
 * catalogue — it reviews the last Lesson they completed, because that is what
 * "how did that go" means to someone who did not name one. The title is the
 * first thing on the screen either way, so which Lesson is being read is never
 * a guess.
 *
 * IT REPORTS WHAT WAS PRACTISED, NOT THE ORDER IT WAS SAT IN. This used to be
 * a transcript: one numbered row per Exercise, each carrying its own verdict,
 * in the sequence the Lesson holds them. That answers a question nobody has —
 * "what was exercise 05" is not how anyone remembers a practice — while the
 * question they do have, WHAT DO I STILL NOT HEAR, had to be reconstructed by
 * reading every row and remembering which tokens carried an ✕. So the rows are
 * gone and what they held is grouped by the thing being practised: the figures
 * first, and under them the two lists that answer it outright.
 *
 * Grouping is also what makes the same sound asked twice legible. A transcript
 * reported it as two unrelated rows; here it is one entry with a count, so a
 * note missed once and caught the next time reads as exactly that.
 *
 * Every figure and every token is read from a RECORDED ATTEMPT, so an Exercise
 * the learner has not reached yet contributes a number to "ainda por
 * responder" and nothing else — reviewing a half-finished Lesson shows how far
 * it got, not the answers to the rest of it.
 *
 * Each answer is named by its own Exercise Type through `describeAnswer`
 * (`../exercise-types/registry.ts`), never by a branch here. This screen knows
 * that an answer was right or wrong — which is a field of the review response
 * — and nothing whatever about notes, audio or options (SC-009).
 */

const EMPTY_REVIEW: LessonReviewResponse = {
  lesson: { id: '', title: '' },
  completed: false,
  exercises: [],
};

/**
 * One thing practised, however many times the Lesson asked for it.
 *
 * `total` is how often it was answered and `hits` how often it was answered
 * right, which is the whole of what separates the two lists below: anything
 * short of every time still has something left in it.
 */
interface Tally {
  readonly token: string;
  readonly hits: number;
  readonly total: number;
}

interface ReviewTallies {
  readonly recognised: readonly Tally[];
  readonly escaped: readonly Tally[];
}

/**
 * Groups the recorded answers by what they were about. First appearance sets
 * the order: it is the order the learner met them in, and no other order here
 * would mean anything to them.
 */
function tally(exercises: readonly ReviewedExercise[], locale: SupportedLocale): ReviewTallies {
  const byToken = new Map<string, { hits: number; total: number }>();

  for (const entry of exercises) {
    const { answer } = entry;
    if (!answer) continue;

    const description = describeAnswer(
      entry.exerciseType,
      answer.publicPrompt,
      answer.publicFeedback,
      locale,
    );
    /*
      An answer this build cannot name contributes no token — and no line
      about itself either. It is already counted in the figures above, which
      is the honest part; a sentence apologising for it would be the screen
      talking about its own version to a learner who came to read their
      practice.
    */
    if (!description) continue;

    const current = byToken.get(description.token) ?? { hits: 0, total: 0 };
    byToken.set(description.token, {
      hits: current.hits + (answer.correct ? 1 : 0),
      total: current.total + 1,
    });
  }

  const all: Tally[] = [...byToken].map(([token, counts]) => ({ token, ...counts }));
  return {
    recognised: all.filter((item) => item.hits === item.total),
    escaped: all.filter((item) => item.hits < item.total),
  };
}

/** What a tally says beside its token, in the fewest words that stay true. */
function tallyDetail(item: Tally): string {
  if (item.total === 1) return item.hits === 1 ? 'você acertou' : 'escapou';
  return `${item.hits} de ${item.total}`;
}

/**
 * The Lesson to review when the learner named none.
 *
 * The catalogue is ordered, and `completed` is the server's own word, so the
 * last Lesson carrying it is the last one they finished. A learner with
 * nothing finished gets `null`, which is a real state — the screen says so
 * rather than failing.
 */
async function lastCompletedLessonId(): Promise<string | null> {
  const { lessons } = await api.lessons();
  const completed = lessons.filter((lesson) => lesson.completed);
  return completed[completed.length - 1]?.id ?? null;
}

export function LessonReview() {
  const navigate = useNavigate();
  /*
   * The Lesson the spine sent, if it sent one. It is state and not a path
   * segment on purpose (see the header): the id is a detail between two
   * screens, not something the learner should have to read or carry.
   */
  const routeState = useLocation().state as { lessonId?: string; from?: string } | null;
  const requestedLessonId = routeState?.lessonId ?? null;
  /*
   * HOW THE LEARNER GOT HERE DECIDES WHAT THE SCREEN OFFERS.
   *
   * Opened from the spine, this is a look back at a node they chose, and the
   * one thing to do with it is sit it again. Arriving from the practice loop
   * it is the close of a Lesson they have just this moment finished, and the
   * cursor is already standing in the next one — so "carry on" is the
   * decision, and "do it again" is the alternative to it.
   *
   * § 1.6 still holds either way: one PRIMARY control, and the arrival is
   * what picks which.
   */
  const fromPractice = routeState?.from === 'practice';
  const locale = useCurrentUser()?.locale ?? DEFAULT_LOCALE;

  const [confirming, setConfirming] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [restartFailed, setRestartFailed] = useState(false);

  const loadReview = useCallback(async (): Promise<LessonReviewResponse> => {
    const id = requestedLessonId ?? (await lastCompletedLessonId());
    // Nothing finished yet is not a failure to load: it is an answer, and
    // EMPTY_REVIEW is how this screen carries it to the render below.
    if (!id) return EMPTY_REVIEW;
    return api.lessonReview(id);
  }, [requestedLessonId]);

  const { status, data, showLoading, reload } = useAsyncData(loadReview, EMPTY_REVIEW, [
    requestedLessonId,
  ]);

  /*
   * RESTARTING IS ONE REQUEST, AND THE SERVER MOVES THE CURSOR.
   *
   * `POST …/restart` drops this Lesson's completions, stamps its recorded
   * attempts as belonging to the run just closed, and points the session at
   * its first Exercise — in one transaction. So there is nothing for this
   * screen to sequence and nothing to undo if it fails: either the learner
   * arrives at `/praticar` standing on the first Exercise, or nothing
   * happened at all and the line below says so.
   */
  const reviewedLessonId = data.lesson.id;

  const onRestart = useCallback(() => {
    if (!reviewedLessonId || restarting) return;
    setRestartFailed(false);
    setRestarting(true);
    void api.restartLesson(reviewedLessonId).then(
      () => {
        setConfirming(false);
        navigate('/praticar');
      },
      () => {
        setRestarting(false);
        setRestartFailed(true);
      },
    );
  }, [reviewedLessonId, restarting, navigate]);

  if (status === 'loading') {
    return showLoading ? <LoadingState label="Carregando suas respostas" /> : null;
  }

  if (status === 'unavailable') {
    return (
      <div className={styles.page}>
        <OfflineState
          title="Não foi possível carregar suas respostas"
          onRetry={() => reload({ force: true })}
        />
      </div>
    );
  }

  /*
   * Nothing to review: the learner reached this address — typed, refreshed,
   * bookmarked — before finishing a single Lesson. There is no Lesson to name
   * and no figures to draw, so the screen says the one true thing and points
   * at the spine, which is where the work is.
   */
  if (!reviewedLessonId) {
    return (
      <div className={styles.page}>
        <header className={styles.greeting}>
          <div className={styles.reviewBack}>
            <Button variant="ghost" icon="arrow-left" size="compact" onClick={() => navigate('/')}>
              Voltar ao caminho
            </Button>
          </div>

          <Text as="h1" variant="display-xl">
            Nada para rever ainda
          </Text>
        </header>

        <Text as="p" variant="body" tone="muted">
          Assim que você concluir uma lição, ela aparece aqui com tudo o que você respondeu.
        </Text>
      </div>
    );
  }

  const answered = data.exercises.filter((entry) => entry.answer !== null).length;
  const correct = data.exercises.filter((entry) => entry.answer?.correct).length;
  /*
    Not "everything that is not answered": an Exercise the server reports as
    complete was practised, whether or not an attempt of it survives in a form
    this build can read, and counting it as still to do would tell the learner
    they have work waiting that they have already done.
  */
  const pending = data.exercises.filter((entry) => !entry.answer && !entry.completed).length;
  /*
    STILL TO GET RIGHT — which is not the same as "not yet answered".

    `pending` above counts Exercises with no answer at all. Since FR-082 an
    Exercise can be answered, answered INCORRECTLY, and still be outstanding:
    the attempt is recorded, no completion is written, and the Lesson holds it
    back for another pass. Counting those needs completion alone, because that
    is the only thing that says "this one is behind you".
  */
  const outstanding = data.exercises.filter((entry) => !entry.completed).length;
  const accuracy = answered > 0 ? Math.round((correct / answered) * 100) : 0;
  const { recognised, escaped } = tally(data.exercises, locale);

  return (
    <div className={styles.page}>
      <header className={styles.greeting}>
        <div className={styles.reviewBack}>
          <Button
            variant="ghost"
            icon="arrow-left"
            size="compact"
            /*
              Back to the spine with THIS Lesson open, not with the cursor's.
              The learner got here by opening a finished node, and returning
              them to a different node would read as the spine having moved
              while they were away.
            */
            onClick={() => navigate('/', { state: { lessonId: reviewedLessonId } })}
          >
            Voltar ao caminho
          </Button>
        </div>

        <Text as="h1" variant="display-xl">
          {data.lesson.title}
        </Text>
      </header>

      {answered === 0 ? (
        <Text as="p" variant="body" tone="muted">
          Você ainda não respondeu nenhum exercício desta lição.
        </Text>
      ) : (
        <>
          {/*
            § 14 — every figure here is one the recorded attempts actually
            carry. The percentage is the one thing the counts alone do not
            say: six certas means something different out of eight than out of
            twenty, and the tile beside it keeps both readable at once.
          */}
          <ul className={styles.reviewStats} aria-label="Como foi esta lição">
            <li className={styles.reviewStat}>
              <Text variant="numeric-display">{accuracy}%</Text>
              <Text variant="caption">de acerto</Text>
            </li>
            <li className={styles.reviewStat}>
              <Text variant="numeric-display">{correct}</Text>
              <Text variant="caption">
                {correct === 1 ? 'certa' : 'certas'} de {answered}
              </Text>
            </li>
            {pending > 0 ? (
              <li className={styles.reviewStat}>
                <Text variant="numeric-display">{pending}</Text>
                <Text variant="caption">ainda por responder</Text>
              </li>
            ) : null}
          </ul>

          {/*
            What got away comes first, because it is what the learner opened
            this screen to find out and what the one action at its foot is
            for. `near-miss` amber and never red: a wrong answer is a normal
            outcome of practice (§ 4, § 14).
          */}
          {escaped.length > 0 ? (
            <section className={styles.reviewSection} aria-labelledby="review-escaped">
              <Text as="h2" id="review-escaped" variant="subheading">
                {escaped.length === 1 ? 'A que escapou' : 'As que escaparam'}
              </Text>

              <ul className={styles.reviewTokens}>
                {escaped.map((item) => (
                  <li key={item.token} className={styles.reviewToken} data-outcome="miss">
                    <Text variant="token-lg">{item.token}</Text>
                    <Text variant="body-sm">{tallyDetail(item)}</Text>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {recognised.length > 0 ? (
            <section className={styles.reviewSection} aria-labelledby="review-recognised">
              <Text as="h2" id="review-recognised" variant="subheading">
                {recognised.length === 1 ? 'A que você reconheceu' : 'As que você reconheceu'}
              </Text>

              <ul className={styles.reviewTokens}>
                {recognised.map((item) => (
                  <li key={item.token} className={styles.reviewToken} data-outcome="correct">
                    <Text variant="token-lg">{item.token}</Text>
                    <Text variant="body-sm">{tallyDetail(item)}</Text>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

        </>
      )}

      {/*
        § 1.6 — one decision. The way back is a ghost control in the header
        above, so the foot of this screen carries the single thing it is
        offering: carry on for a learner who has just finished the Lesson,
        do it again for one who came back to look at it.
      */}
      {data.completed ? (
        <section className={styles.reviewAction}>
          {fromPractice ? (
            <>
              <Text as="p" variant="body-sm" tone="muted">
                Sua próxima lição já está esperando por você.
              </Text>
              <Button
                variant="primary-gradient"
                icon="play"
                onClick={() => navigate('/praticar')}
              >
                Continuar praticando
              </Button>
            </>
          ) : null}

          <Text as="p" variant="body-sm" tone="muted">
            Praticar de novo devolve esta lição ao início. Tudo o que você já respondeu continua
            registrado — o que recomeça é a lição, não o seu histórico.
          </Text>
          <Button
            variant={fromPractice ? 'ghost' : undefined}
            icon="rotate-ccw"
            onClick={() => setConfirming(true)}
          >
            Praticar de novo
          </Button>
          {restartFailed ? (
            <Text as="p" variant="body-sm" tone="muted">
              Não foi possível recomeçar a lição agora. Tente novamente.
            </Text>
          ) : null}
        </section>
      ) : fromPractice ? (
        /*
          THE PASS ENDED, THE LESSON DID NOT.

          A Lesson is left only once every Exercise in it has been answered
          correctly (FR-082), so reaching the end of a pass with something
          outstanding sends the learner back to the first one they missed —
          and the practice loop hands them here on the way, exactly as it does
          for a finished Lesson. Without this branch that arrival is a screen
          with no way on, which is the version of "stuck" this screen exists
          to prevent.

          "Praticar de novo" is deliberately absent: it restarts a FINISHED
          Lesson, and the server refuses it on one still in progress
          (`409 lesson_not_complete`). The way on here is simply forward —
          the cursor is already standing on the Exercise that comes next.

          Gated on `fromPractice` for the same reason the completed branch
          gates its own continue: arriving from the spine, the learner's cursor
          may be in another Lesson entirely, and a button promising to carry on
          with THIS one would take them somewhere else.
        */
        <section className={styles.reviewAction}>
          <Text as="p" variant="body-sm" tone="muted">
            {outstanding === 1
              ? 'Falta 1 exercício para concluir esta lição. Ele volta com uma nova nota.'
              : `Faltam ${outstanding} exercícios para concluir esta lição. Eles voltam com novas notas.`}
          </Text>
          <Button variant="primary-gradient" icon="play" onClick={() => navigate('/praticar')}>
            Continuar praticando
          </Button>
        </section>
      ) : null}

      {/*
        Asked rather than done. Restarting moves the learner's position
        backwards through work they had finished, which is the one thing on
        this screen they cannot simply undo by pressing something else.
      */}
      <Dialog
        open={confirming}
        title="Praticar esta lição de novo?"
        onClose={() => setConfirming(false)}
        testId="confirm-restart"
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Agora não
            </Button>
            {restarting ? (
              <Button disabled disabledReason="Recomeçando a lição." icon="rotate-ccw">
                Recomeçar
              </Button>
            ) : (
              <Button variant="primary-gradient" icon="rotate-ccw" onClick={onRestart}>
                Recomeçar
              </Button>
            )}
          </>
        }
      >
        <Text as="p" variant="body">
          Sua prática volta para o primeiro exercício de {data.lesson.title}. As respostas que você
          já deu continuam guardadas, e esta revisão passa a mostrar as novas.
        </Text>
      </Dialog>
    </div>
  );
}
